import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import workingHoursService from '../../api/workingHoursService';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  getWorkingHoursScreenPermissions,
  hasAnyWorkingHoursAccess,
} from '../../utils/workingHoursScreenPermissions';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import {
  Clock,
  Calendar,
  RefreshCw,
  Loader2,
  Save,
  Plus,
  Edit,
  Trash2,
  ShieldAlert,
} from 'lucide-react';

const DAY_DEFS = [
  { dow: 1, label: 'Monday' },
  { dow: 2, label: 'Tuesday' },
  { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' },
  { dow: 5, label: 'Friday' },
  { dow: 6, label: 'Saturday' },
  { dow: 7, label: 'Sunday' },
];

const defaultDayRow = (dow, working = dow <= 5) => ({
  dayOfWeek: dow,
  isWorkingDay: working,
  startTime: working ? '09:00' : '',
  endTime: working ? '17:00' : '',
});

const buildDefaultSchedule = () => DAY_DEFS.map((d) => defaultDayRow(d.dow));

const toTimeInput = (value) => {
  if (!value) return '';
  const s = String(value);
  return s.length >= 5 ? s.slice(0, 5) : s;
};

const mapScheduleToForm = (schedule) => {
  const byDow = new Map((schedule?.days || []).map((d) => [Number(d.dayOfWeek), d]));
  return DAY_DEFS.map(({ dow }) => {
    const row = byDow.get(dow);
    if (!row) return defaultDayRow(dow, false);
    const working = row.isWorkingDay === true;
    return {
      dayOfWeek: dow,
      isWorkingDay: working,
      startTime: working ? toTimeInput(row.startTime) : '',
      endTime: working ? toTimeInput(row.endTime) : '',
    };
  });
};

const WorkingHoursManagement = () => {
  const permissions = useMemo(() => getWorkingHoursScreenPermissions(), []);
  const canAccess = hasAnyWorkingHoursAccess();
  const canSave = permissions.canInsert || permissions.canUpdate;
  const canManageHolidays = permissions.canInsert || permissions.canUpdate || permissions.canDelete;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduleConfigured, setScheduleConfigured] = useState(false);
  const [scheduleId, setScheduleId] = useState(null);
  const [days, setDays] = useState(buildDefaultSchedule);
  const [holidays, setHolidays] = useState([]);
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ holidayDate: '', holidayName: '' });
  const [holidaySaving, setHolidaySaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const loadData = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const scheduleResp = await workingHoursService.getSchedule();
      const schedule = scheduleResp?.data ?? scheduleResp;
      if (schedule && schedule.scheduleId) {
        setScheduleConfigured(true);
        setScheduleId(schedule.scheduleId);
        setDays(mapScheduleToForm(schedule));
      } else {
        setScheduleConfigured(false);
        setScheduleId(null);
        setDays(buildDefaultSchedule());
      }
    } catch (error) {
      setLoadError(extractErrorMessage(error, 'Failed to load working hours schedule'));
      setScheduleConfigured(false);
      setDays(buildDefaultSchedule());
    }

    try {
      setHolidayLoading(true);
      const holidayResp = await workingHoursService.listHolidays();
      const list = holidayResp?.data ?? holidayResp;
      setHolidays(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load public holidays'));
      setHolidays([]);
    } finally {
      setHolidayLoading(false);
      setLoading(false);
    }
  }, [canAccess]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDayToggle = (index, working) => {
    setDays((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        isWorkingDay: working,
        startTime: working ? (next[index].startTime || '09:00') : '',
        endTime: working ? (next[index].endTime || '17:00') : '',
      };
      return next;
    });
  };

  const handleDayTimeChange = (index, field, value) => {
    setDays((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const validateDaysClient = () => {
    const workingDays = days.filter((d) => d.isWorkingDay);
    if (workingDays.length === 0) {
      toast.error('At least one working day is required');
      return false;
    }
    for (const day of workingDays) {
      if (!day.startTime || !day.endTime) {
        toast.error('Working days require start and end times');
        return false;
      }
      if (day.startTime >= day.endTime) {
        toast.error('Start time must be before end time for each working day');
        return false;
      }
    }
    return true;
  };

  const handleSaveSchedule = async () => {
    if (!canSave) {
      toast.error('You do not have permission to save the schedule');
      return;
    }
    if (!validateDaysClient()) return;

    setSaving(true);
    try {
      const payload = days.map((d) => ({
        dayOfWeek: d.dayOfWeek,
        isWorkingDay: d.isWorkingDay,
        startTime: d.isWorkingDay ? d.startTime : null,
        endTime: d.isWorkingDay ? d.endTime : null,
      }));
      const resp = await workingHoursService.saveSchedule(payload);
      const saved = resp?.data ?? resp;
      setScheduleConfigured(true);
      setScheduleId(saved?.scheduleId ?? scheduleId);
      setDays(mapScheduleToForm(saved));
      toast.success(scheduleConfigured ? 'Schedule updated' : 'Schedule created');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save schedule'));
    } finally {
      setSaving(false);
    }
  };

  const openHolidayModal = (holiday = null) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setHolidayForm({
        holidayDate: holiday.holidayDate || '',
        holidayName: holiday.holidayName || '',
      });
    } else {
      setEditingHoliday(null);
      setHolidayForm({ holidayDate: '', holidayName: '' });
    }
    setHolidayModalOpen(true);
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.holidayDate || !holidayForm.holidayName.trim()) {
      toast.error('Holiday date and name are required');
      return;
    }
    if (editingHoliday && !permissions.canUpdate) {
      toast.error('You do not have permission to edit holidays');
      return;
    }
    if (!editingHoliday && !permissions.canInsert) {
      toast.error('You do not have permission to add holidays');
      return;
    }

    setHolidaySaving(true);
    try {
      const payload = {
        holidayDate: holidayForm.holidayDate,
        holidayName: holidayForm.holidayName.trim(),
      };
      if (editingHoliday?.holidayId) {
        await workingHoursService.updateHoliday(editingHoliday.holidayId, payload);
        toast.success('Holiday updated');
      } else {
        await workingHoursService.createHoliday(payload);
        toast.success('Holiday added');
      }
      setHolidayModalOpen(false);
      const holidayResp = await workingHoursService.listHolidays();
      const list = holidayResp?.data ?? holidayResp;
      setHolidays(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save holiday'));
    } finally {
      setHolidaySaving(false);
    }
  };

  const handleToggleHolidayActive = async (holiday) => {
    if (!permissions.canUpdate) {
      toast.error('You do not have permission to update holidays');
      return;
    }
    try {
      await workingHoursService.updateHolidayStatus(holiday.holidayId, !holiday.isActive);
      toast.success(holiday.isActive ? 'Holiday deactivated' : 'Holiday activated');
      const holidayResp = await workingHoursService.listHolidays();
      const list = holidayResp?.data ?? holidayResp;
      setHolidays(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update holiday status'));
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    if (!permissions.canDelete) {
      toast.error('You do not have permission to remove holidays');
      return;
    }
    if (!window.confirm(`Remove holiday "${holiday.holidayName}"?`)) return;
    try {
      await workingHoursService.deleteHoliday(holiday.holidayId);
      toast.success('Holiday removed');
      setHolidays((prev) => prev.filter((h) => h.holidayId !== holiday.holidayId));
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to remove holiday'));
    }
  };

  if (!canAccess) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            You do not have permission to view Working Hours configuration. Ask an administrator to
            grant access via Role Management.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Working Hours</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure tenant working days and public holidays for Business Hours SLA (D1).
          </p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={saving}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loadError && (
        <Alert variant="destructive">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {!scheduleConfigured && (
        <Alert>
          <AlertDescription>
            No working hours schedule is configured yet. Set your weekly hours below and save to
            create the tenant schedule.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5" />
            Weekly Schedule
            {scheduleConfigured && <Badge variant="secondary">Active</Badge>}
          </CardTitle>
          {canSave && (
            <Button onClick={handleSaveSchedule} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {scheduleConfigured ? 'Update Schedule' : 'Create Schedule'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {DAY_DEFS.map(({ dow, label }, index) => {
            const row = days[index];
            return (
              <div
                key={dow}
                className="grid grid-cols-1 md:grid-cols-[140px_100px_1fr_1fr] gap-3 items-center border rounded-lg p-3"
              >
                <div className="font-medium">{label}</div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={row.isWorkingDay}
                    onCheckedChange={(checked) => handleDayToggle(index, checked)}
                    disabled={!canSave}
                  />
                  <span className="text-sm text-muted-foreground">
                    {row.isWorkingDay ? 'Working' : 'Off'}
                  </span>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Start</Label>
                  <Input
                    type="time"
                    value={row.startTime}
                    onChange={(e) => handleDayTimeChange(index, 'startTime', e.target.value)}
                    disabled={!row.isWorkingDay || !canSave}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End</Label>
                  <Input
                    type="time"
                    value={row.endTime}
                    onChange={(e) => handleDayTimeChange(index, 'endTime', e.target.value)}
                    disabled={!row.isWorkingDay || !canSave}
                  />
                </div>
              </div>
            );
          })}
          {!canSave && (
            <p className="text-sm text-muted-foreground">Read-only: you do not have insert/update permission.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" />
            Public Holidays
          </CardTitle>
          {permissions.canInsert && (
            <Button variant="outline" onClick={() => openHolidayModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Holiday
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {holidayLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : holidays.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No public holidays configured.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Status</th>
                    {canManageHolidays && <th className="py-2">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {holidays.map((h) => (
                    <tr key={h.holidayId} className="border-b last:border-0">
                      <td className="py-2 pr-4">{h.holidayDate}</td>
                      <td className="py-2 pr-4">{h.holidayName}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={h.isActive ? 'default' : 'secondary'}>
                          {h.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      {canManageHolidays && (
                        <td className="py-2 flex flex-wrap gap-2">
                          {permissions.canUpdate && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => openHolidayModal(h)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleToggleHolidayActive(h)}>
                                {h.isActive ? 'Deactivate' : 'Activate'}
                              </Button>
                            </>
                          )}
                          {permissions.canDelete && (
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteHoliday(h)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={holidayModalOpen}
        onClose={() => setHolidayModalOpen(false)}
        title={editingHoliday ? 'Edit Public Holiday' : 'Add Public Holiday'}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="holidayDate">Date</Label>
            <Input
              id="holidayDate"
              type="date"
              value={holidayForm.holidayDate}
              onChange={(e) => setHolidayForm((f) => ({ ...f, holidayDate: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="holidayName">Name</Label>
            <Input
              id="holidayName"
              value={holidayForm.holidayName}
              maxLength={255}
              onChange={(e) => setHolidayForm((f) => ({ ...f, holidayName: e.target.value }))}
              placeholder="e.g. New Year's Day"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setHolidayModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveHoliday} disabled={holidaySaving}>
              {holidaySaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WorkingHoursManagement;
